// MapComponent.js
import React, { useState, useEffect, useRef } from 'react';
import {
  MapContainer,
  TileLayer,
  WMSTileLayer,
  LayersControl,
  useMap,
  useMapEvents,
  Popup
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import Legend from './Legend';

const vginItems = [
  { label: 'Water',                  color: '#00A9E6' },
  { label: 'Impervious Extracted',   color: '#343434' },
  { label: 'Impervious Local Datasets', color: '#000000' },
  { label: 'Barren',                 color: '#CCCCCC' },
  { label: 'Forest',                 color: '#267300' },
  { label: 'Tree',                   color: '#38A800' },
  { label: 'Shrub/Scrub',            color: '#737300' },
  { label: 'Harvested/Disturbed',    color: '#9E2521' },
  { label: 'TurfGrass',              color: '#CDAA66' },
  { label: 'Pasture',                color: '#98E600' },
  { label: 'Cropland',               color: '#E6E600' },
  { label: 'NWI/Other',              color: '#894465' }
];

const lcItems = [
  { label: 'Ag→Urban',    color: '#FF0000' },
  { label: 'Forest→Urban', color: '#00FF00' },
  { label: 'Forest→Ag',    color: '#0000FF' }
];

const ndciItems = [
  { label: 'Bloom', color: '#FF0000' }
];

// ─── GeoServer base URLs ───
const base = {
  sites: 'http://52.5.129.122/geoserver/smlworkspace/wms',
  nlcd:  'http://52.5.129.122/geoserver/nlcd/wms',
  vgin:  'http://52.5.129.122/geoserver/vgin1m/wms',
  ndci:  'http://52.5.129.122/geoserver/ndci/wms',
  shp:   'http://52.5.129.122/geoserver/sml_shapefiles/wms'
};


// ─── Legend URL for VGIN ───
const vginLegendUrl = [
  base.vgin,
  '?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png',
  '&LAYER=vgin1m:tiled_output',
  '&STYLE=vgin1m:vgin_style',
  '&LEGEND_OPTIONS=' +
    [
      'legendTitle:'  + encodeURIComponent('VGIN1m Land Cover (2016)'),
      'fontSize:10',
      'symbolWidth:8',
      'symbolHeight:8'
    ].join(';')
].join('');





// ─── A reusable layer + legend wrapper ───
function LayerWithLegend({
  url,
  layers,
  items = null,
  columns = 1,
  legendUrl,
  title = 'Legend',
  opacity = 0.6
}) {
  const layerRef = useRef();
  const map = useMap();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const layer = layerRef.current?.leafletElement || layerRef.current;
    if (!layer) return;
    const onChange = () => setVisible(map.hasLayer(layer));
    map.on('overlayadd overlayremove', onChange);
    onChange();
    return () => map.off('overlayadd overlayremove', onChange);
  }, [map]);

  return (
    <>
      <WMSTileLayer
        ref={layerRef}
        url={url}
        layers={layers}
        format="image/png"
        transparent
        opacity={opacity}
      />

      {visible && (
        items
          ? <Legend title={title} items={items} columns={columns} />
          : <Legend title={title} url={legendUrl} />
      )}
    </>
  );
}

const MapComponent = ({ onMapClick }) => {
  const [popupPosition, setPopupPosition] = useState(null);
  const [popupContent,  setPopupContent]  = useState('');

  const MapClickHandler = () => {
    const map = useMap();
    useMapEvents({
      click: (e) => {
        const { lat, lng } = e.latlng;
        const bounds = map.getBounds();
        const bbox = [
          bounds.getWest(),
          bounds.getSouth(),
          bounds.getEast(),
          bounds.getNorth()
        ].join(',');
        const { x, y } = e.containerPoint;
        const { x: width, y: height } = map.getSize();

        const buildGFI = (url, layer) =>
          `${url}?service=WMS&version=1.1.0&request=GetFeatureInfo` +
          `&layers=${layer}&query_layers=${layer}` +
          `&bbox=${bbox}&srs=EPSG:4326` +
          `&width=${width}&height=${height}` +
          `&X=${Math.floor(x)}&Y=${Math.floor(y)}` +
          `&info_format=application/json`;

        // Only five endpoints now
        const urls = {
          sites:  buildGFI(base.sites,  'smlworkspace:sites'),
	  newsites: buildGFI(base.sites,  'smlworkspace:newsites'),
          nlcd:   buildGFI(base.nlcd,   'nlcd:l3class_3857'),
          ndci:   buildGFI(base.ndci,   'ndci:ndci20230602'),
          vgin:   buildGFI(base.vgin,   'vgin1m:tiled_output'),
          lake:   buildGFI(base.shp,    'sml_shapefiles:smllake_ll'),
          rivers: buildGFI(base.shp,    'sml_shapefiles:tworivers_ll')
        };



        Promise.all(
          Object.values(urls).map(u => fetch(u).then(r => r.json()))
        )
        .then(([dataSites, dataNewSites,dataNLCD, dataVGIN, dataLake, dataRivers]) => {
          let content = '';

        if (dataSites.features?.length) {
  const p = dataSites.features[0].properties;
  content += `<b>Site ID:</b> ${p.fdt_sta_id}<br/>` +
             `<b>Name:</b> ${p.sta_desc}<br/><br/>`;

// 2️⃣ Only if no Site, check Ferrum Sites
} else if (dataNewSites.features?.length) {
  const p = dataNewSites.features[0].properties;
  content += `<b>Ferrum Site ID:</b> ${p.fdt_sta_id}<br/>` +
             `<b>Description:</b> ${p.sta_desc}<br/><br/>`;

// 3️⃣ If neither, show “none”
} else {
  content += `<b>Sites:</b> none<br/><br/>`;
}


          // --- NLCD Change ---
          const nlcdLabels = { 0: "No Change", 1: "Ag→Urban", 2: "Forest→Urban", 3: "Forest→Ag" };
          if (dataNLCD.features?.length) {
            const v = dataNLCD.features[0].properties.GRAY_INDEX;
            content += `<b>NLCD:</b> ${v} (${nlcdLabels[v]||'–'})<br/><br/>`;
          } else {
            content += `<b>NLCD:</b> none<br/><br/>`;
          }

          // --- VGIN1m ---
          const vginLabels = {
            0:"No Data",11:"Water",21:"Impervious",22:"Impervious",
            31:"Barren",41:"Forest",42:"Tree",51:"Shrub",61:"Disturbed",
            71:"TurfGrass",81:"Pasture",82:"Cropland",91:"NWI/Other"
          };
          if (dataVGIN.features?.length) {
            const v = dataVGIN.features[0].properties.PALETTE_INDEX;
            content += `<b>VGIN1m:</b> ${v} (${vginLabels[v]||'–'})<br/><br/>`;
          } else {
            content += `<b>VGIN1m:</b> none<br/><br/>`;
          }

          // --- Lake & Rivers (if no site) ---
          const hasSite = dataSites.features?.length > 0;
          if (!hasSite) {
            if (dataLake.features?.length) {
              const lakeFeat = dataLake.features[0].properties;
              const lakeId = lakeFeat.fid ?? dataLake.features[0].id;
              content += `<b>Smith Mountain Lake ID:</b> ${lakeId}<br/><br/>`;
            } else {
              content += `<b>Smith Mountain Lake:</b> none<br/><br/>`;
            }
            if (dataRivers.features?.length) {
              const riverFeat = dataRivers.features[0].properties;
              const riverId = riverFeat.fid ?? dataRivers.features[0].id;
              content += `<b>River Feature ID:</b> ${riverId}<br/><br/>`;
            } else {
              content += `<b>Rivers:</b> none<br/><br/>`;
            }
          }

          // --- Primary selection callback ---
                    // --- Primary selection callback (Sites ➔ Ferrum Sites ➔ Lake ➔ Rivers) ---
          let primaryId, primaryName, primaryLayer;
          if (dataSites.features?.length) {
            // original Sites take priority
            const p = dataSites.features[0].properties;
            primaryId    = p.fdt_sta_id;
            primaryName  = p.sta_desc;
            primaryLayer = 'sites';

          } else if (dataNewSites.features?.length) {
            // then Ferrum Sites
            const p = dataNewSites.features[0].properties;
            primaryId    = p.fdt_sta_id;
            primaryName  = p.sta_desc;
            primaryLayer = 'newsites';

          } else if (dataLake.features?.length) {
            // then Lake
            const lakeFeat = dataLake.features[0].properties;
            primaryId    = lakeFeat.fid ?? dataLake.features[0].id;
            primaryName  = 'Smith Mountain Lake';
            primaryLayer = 'lake';

          } else if (dataRivers.features?.length) {
            // then Rivers
            const riverFeat = dataRivers.features[0].properties;
            primaryId    = riverFeat.fid ?? dataRivers.features[0].id;
            primaryName  = 'River';
            primaryLayer = 'river';
          }

          if (primaryId) {
            onMapClick(lat, lng, primaryId, primaryName, primaryLayer);
          }

          setPopupPosition([lat, lng]);
          setPopupContent(content);
        })
        .catch(err => {
          console.error('GFI error', err);
          setPopupPosition([lat, lng]);
          setPopupContent('Error fetching data.');
        });
      }
    });
    return null;
  };

  return (
    <MapContainer center={[37.2, -80]} zoom={10} style={{ height: '90vh', width: '100%' }}>
      <LayersControl position="topright" collapsed={false}>

        <LayersControl.BaseLayer name="OpenStreetMap" checked>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="© OpenStreetMap contributors"
          />
        </LayersControl.BaseLayer>

        <LayersControl.Overlay name="Sites" checked>
          <WMSTileLayer
            url={base.sites}
            layers="smlworkspace:sites"
            format="image/png"
            transparent
          />
        </LayersControl.Overlay>

<LayersControl.Overlay name="Ferrum Sites" checked={false}>
  <WMSTileLayer
    url={base.sites}
    layers="smlworkspace:newsites"
    format="image/png"
    transparent
    opacity={0.6}
  />
</LayersControl.Overlay>





        <LayersControl.Overlay name="VGIN1m Land Cover (2016)">
          <LayerWithLegend
            url={base.vgin}
            layers="vgin1m:tiled_output"
            items={vginItems}
            columns={2}
            title="VGIN1m Land Cover (2016)"
            opacity={0.6}
            legendUrl={vginLegendUrl}
          />
        </LayersControl.Overlay>

     <LayersControl.Overlay name="NDCI Index (2023-06-02)">
          <LayerWithLegend
            url={base.ndci}
            layers="ndci:ndci20230602"
            items={ndciItems}
            title="NDCI (2023-06-02)"
            columns={1}
            opacity={0.6}
          />
        </LayersControl.Overlay>



        <LayersControl.Overlay name="Land Cover Change (2001-2023)">
          <LayerWithLegend
            url={base.nlcd}
            layers="nlcd:l3class_3857"
            items={lcItems}
            columns={3}
            title="Land Cover Change (2001-2023)"
            opacity={0.6}
          />
        </LayersControl.Overlay>

        <LayersControl.Overlay name="Smith Mountain Lake">
          <WMSTileLayer
            url={base.shp}
            layers="sml_shapefiles:smllake_ll"
            format="image/png"
            transparent
            opacity={0.6}
          />
        </LayersControl.Overlay>

        <LayersControl.Overlay name="Rivers">
          <WMSTileLayer
            url={base.shp}
            layers="sml_shapefiles:tworivers_ll"
            format="image/png"
            transparent
            opacity={0.6}
          />
        </LayersControl.Overlay>

<LayersControl.Overlay name="SML Basin">
          <WMSTileLayer
            url={base.shp}
            layers="sml_shapefiles:smlbasin"
            format="image/png"
            transparent
            opacity={0.6}
          />
        </LayersControl.Overlay>


      </LayersControl>

      <MapClickHandler />

      {popupPosition && (
        <Popup position={popupPosition} onClose={() => setPopupPosition(null)}>
          <div dangerouslySetInnerHTML={{ __html: popupContent }} />
        </Popup>
      )}
    </MapContainer>
  );
};

export default MapComponent;
